import type { ScreenUICopy } from '@polyai/ui/uiCopy/types';

const SCREEN_UI_COPY: Omit<ScreenUICopy<string>, 'pageDescription'> = {
  pageTitle: 'Data access',
};

export const DATA_ACCESS = {
  ...SCREEN_UI_COPY,
  empty: {
    description: 'Press the "API key" button to get started.',
    title: 'You have no API keys',
  },
  noResults: {
    title: 'No results',
    searchDescription: (searchTerm: string) =>
      `No API keys match "${searchTerm}". Try a different search.`,
    filterDescription:
      'No API keys found for the selected filters. Try adjusting your date filters.',
    searchAndFilterDescription: (searchTerm: string) =>
      `No API keys match "${searchTerm}" with the current filters. Try adjusting your search or filters.`,
  },
  error: {
    loading: 'Error loading API keys',
  },
  button: {
    configuration: 'Configuration',
    addApiKey: 'API key',
  },
  modal: {
    delete: {
      title: 'Delete this API key?',
      subtitle: (isApiKeysTabEnabled: boolean) =>
        isApiKeysTabEnabled
          ? 'This action cannot be undone and will remove access for your services and products using this key.'
          : 'This will permanently delete the key',
      button: {
        confirm: 'Delete',
      },
    },
    add: {
      title: 'Create API key',
      subtitle: (isApiKeysTabEnabled: boolean) =>
        isApiKeysTabEnabled
          ? 'This API key will grant programmatic access to your account and allow access to all agents in this workspace.'
          : "Selecting specific agents to grant the API key access to their data. The list of agents can't be changed later. Choosing all agents includes both existing and any new agents automatically.",
      button: {
        addApiKey: 'Add',
        confirm: 'Create',
      },
      callout:
        ' Once you’ve named your API key you won’t be able to change it.',
    },
    view: {
      title: 'Preview API key',
      subtitle:
        "Selecting specific agents to grant the API key access to their data. The list of agents can't be changed later. Choosing all agents includes both existing and any new agents automatically",
      button: {
        done: 'Done',
      },
    },
    select: {
      apiKeys: {
        all: {
          label: 'All agents',
          hint: '(Existing and new)',
        },
      },
      metrics: {
        all: {
          label: 'All metrics',
        },
      },
    },
    apiKeyName: {
      label: 'Name',
      placeHolder: 'API key name',
    },
    projectOptions: {
      label: 'Agents',
      help: 'List of agents whose data can be accessed with this API key.',
    },
    resource_access: {
      title: 'Resource Access',
      error:
        'At least one data resource must be enabled to ensure the API returns usable data.',
      toggles: {
        conversations: {
          text: 'Conversations data',
          tooltip:
            'Access to conversation data, including transcripts and high-level conversation metrics.',
        },
        handoffState: {
          text: 'Handoff',
          tooltip:
            'Access to handoff events, such as when a conversation is transferred to a human agent or another system.',
        },
        externalEvents: {
          text: 'External events',
          tooltip:
            'Access to external events linked to agents, such as triggers from external systems or third-party integrations.',
        },
      },
    },
  },
  apiKeyCard: {
    projectAccessHeader: 'Access',
    expiresHeader: 'Expires',
    projects: {
      all: 'All agents',
      none: 'None',
    },
    apiKeyCopy: {
      success: 'API Key copied',
      error: 'Failed to copy API Key',
    },
    expiration: {
      expired: 'API key is expired',
    },
    overflow: {
      open: 'Open',
      delete: 'Delete',
    },
  },
  filter: {
    title: (isApiKeysTabEnabled: boolean) => {
      return isApiKeysTabEnabled ? 'API keys' : 'sources';
    },
  },
  apiKeysPageTitle: 'API keys',
};
